import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "d-flex justify-space-between align-center pa-4" }
const _hoisted_2 = { class: "d-flex align-center" }
const _hoisted_3 = { class: "mb-4" }
const _hoisted_4 = { class: "text-caption text-grey-darken-1" }
const _hoisted_5 = {
  key: 1,
  class: "mb-4"
}
const _hoisted_6 = { class: "mb-4" }
const _hoisted_7 = { class: "d-flex gap-3" }
const _hoisted_8 = { class: "mb-4" }
const _hoisted_9 = { class: "d-flex gap-3 w-100" }

import { ref, computed, watch } from 'vue';
import { useSettingsStore } from "@/api/store/settings";
import { healthPlannerService, type PlannerEntry } from "@/api/services/healthPlanner";
import { hexToRgba } from "@/tools/design";

interface Props {
  modelValue: boolean;
  plannerEntry?: PlannerEntry | null;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'status-updated'): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PlannerEntryForm',
  props: {
    modelValue: { type: Boolean },
    plannerEntry: {}
  },
  emits: ["update:modelValue", "status-updated"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const settingsStore = useSettingsStore();

// Form state
const dialog = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
});

const comment = ref('');
const isSubmitting = ref(false);
const formError = ref<string | null>(null);
const currentStatus = ref<string | null>(null);

// Helper function to get planner type icon
const getPlannerTypeIcon = (plannerType?: string) => {
  switch (plannerType) {
    case 'Exercise':
      return 'fa fa-star';
    case 'Food':
      return 'fa fa-cutlery';
    case 'Drink':
      return 'fa fa-glass';
    case 'Lifestyle':
      return 'fa fa-heart';
    default:
      return 'fa fa-calendar';
  }
};

// Format the current date and time
const formattedDateTime = computed(() => {
  if (!props.plannerEntry?.scheduled_datetime) return '';
  const date = new Date(props.plannerEntry.scheduled_datetime);
  return date.toLocaleString('default', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
});

const updateStatus = async (status: 'completed' | 'ignored') => {
  if (!props.plannerEntry?.id) return;

  isSubmitting.value = true;
  formError.value = null;

  try {
    // Toggle behavior: if clicking the same status, set it back to pending
    const newStatus = currentStatus.value === status ? 'pending' : status;
    
    await healthPlannerService.updateStatus(
      props.plannerEntry.id, 
      newStatus, 
      props.plannerEntry.scheduled_datetime
    );
    currentStatus.value = newStatus; // Update local status immediately
    emit('status-updated');
    // Don't close dialog - let user add comments or manually close
  } catch (err: any) {
    console.error('Error updating planner entry status:', err);
    formError.value = err.response?.data?.message || 'Failed to update status. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

const saveComment = async () => {
  if (!props.plannerEntry?.id || !comment.value.trim()) return;

  isSubmitting.value = true;
  formError.value = null;

  try {
    await healthPlannerService.saveComment(props.plannerEntry.id, comment.value.trim());
    comment.value = ''; // Clear comment after saving
    emit('status-updated'); // Refresh the entry data
  } catch (err: any) {
    console.error('Error saving comment:', err);
    formError.value = err.response?.data?.message || 'Failed to save comment. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

const closeDialog = () => {
  dialog.value = false;
  formError.value = null;
  // Don't clear comment.value - it will be reset when plannerEntry changes
};

// Watch for planner entry changes to load existing comment and status
watch(() => props.plannerEntry, (newEntry) => {
  comment.value = newEntry?.practitioner_comment || '';
  currentStatus.value = newEntry?.status || null;
  formError.value = null;
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    modelValue: dialog.value,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((dialog).value = $event)),
    width: "500"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, {
        class: "planner-entry-form",
        rounded: "lg"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_v_icon, {
                icon: getPlannerTypeIcon(__props.plannerEntry?.type),
                size: "24",
                color: _unref(settingsStore).settings.buttonColor,
                class: "me-4"
              }, null, 8, ["icon", "color"]),
              _createElementVNode("span", {
                class: "text-h6",
                style: _normalizeStyle({ color: _unref(settingsStore).settings.buttonColor })
              }, _toDisplayString(__props.plannerEntry?.name || ''), 5)
            ]),
            _createVNode(_component_v_btn, {
              icon: "",
              variant: "text",
              onClick: closeDialog
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { icon: "fa fa-times" })
              ]),
              _: 1
            })
          ]),
          _createVNode(_component_v_divider),
          _createVNode(_component_v_card_text, { class: "pa-4" }, {
            default: _withCtx(() => [
              (formError.value)
                ? (_openBlock(), _createBlock(_component_v_alert, {
                    key: 0,
                    type: "error",
                    class: "mb-4"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(formError.value), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, _toDisplayString(formattedDateTime.value), 1)
              ]),
              (__props.plannerEntry?.description)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createElementVNode("div", {
                      class: "text-subtitle-2 mb-2",
                      style: _normalizeStyle({ color: _unref(settingsStore).settings.primaryColor })
                    }, " Description ", 4),
                    _createElementVNode("div", {
                      class: "description-text pa-3",
                      style: _normalizeStyle({ 
            backgroundColor: _unref(hexToRgba)(_unref(settingsStore).settings.primaryColor, 0.05),
            border: `1px solid ${_unref(hexToRgba)(_unref(settingsStore).settings.primaryColor, 0.2)}`,
            borderRadius: '8px'
          })
                    }, _toDisplayString(__props.plannerEntry.description), 5)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", {
                  class: "text-subtitle-2 mb-3",
                  style: _normalizeStyle({ color: _unref(settingsStore).settings.primaryColor })
                }, " Update Status ", 4),
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(_component_v_btn, {
                    variant: currentStatus.value === 'completed' ? 'flat' : 'outlined',
                    color: currentStatus.value === 'completed' ? 'green' : 'grey',
                    rounded: "lg",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (updateStatus('completed'))),
                    disabled: isSubmitting.value,
                    class: "flex-1"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, {
                        start: "",
                        size: "small"
                      }, {
                        default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                          _createTextVNode("fa fa-check", -1)
                        ]))]),
                        _: 1
                      }),
                      _createTextVNode(" " + _toDisplayString(currentStatus.value === 'completed' ? 'Unmark Completed' : 'Mark Completed'), 1)
                    ]),
                    _: 1
                  }, 8, ["variant", "color", "disabled"]),
                  _createVNode(_component_v_btn, {
                    variant: currentStatus.value === 'ignored' ? 'flat' : 'outlined',
                    color: currentStatus.value === 'ignored' ? 'grey-darken-2' : 'grey',
                    rounded: "lg",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (updateStatus('ignored'))),
                    disabled: isSubmitting.value,
                    class: "flex-1"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, {
                        start: "",
                        size: "small"
                      }, {
                        default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                          _createTextVNode("fa fa-times", -1)
                        ]))]),
                        _: 1
                      }),
                      _createTextVNode(" " + _toDisplayString(currentStatus.value === 'ignored' ? 'Unmark Ignored' : 'Mark Ignored'), 1)
                    ]),
                    _: 1
                  }, 8, ["variant", "color", "disabled"])
                ])
              ]),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("div", {
                  class: "text-subtitle-2 mb-2",
                  style: _normalizeStyle({ color: _unref(settingsStore).settings.primaryColor })
                }, " Client Comment ", 4),
                _createVNode(_component_v_textarea, {
                  modelValue: comment.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((comment).value = $event)),
                  label: "Share your thoughts about this planner entry...",
                  style: _normalizeStyle(`label-color: ${_unref(settingsStore).settings.primaryColor}`),
                  variant: "outlined",
                  rows: "4",
                  maxlength: 250,
                  counter: "250",
                  placeholder: "Add your comment here...",
                  class: "mb-2"
                }, null, 8, ["modelValue", "style"])
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, { class: "pa-4" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_9, [
                (comment.value.trim())
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 0,
                      style: _normalizeStyle({
              backgroundColor: _unref(settingsStore).settings.buttonColor,
              color: 'white'
            }),
                      class: "text-white primary-button flex-1",
                      rounded: "lg",
                      onClick: saveComment,
                      disabled: isSubmitting.value
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, {
                          start: "",
                          size: "small"
                        }, {
                          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                            _createTextVNode("fa fa-save", -1)
                          ]))]),
                          _: 1
                        }),
                        _cache[7] || (_cache[7] = _createTextVNode(" Save Comment ", -1))
                      ]),
                      _: 1
                    }, 8, ["style", "disabled"]))
                  : _createCommentVNode("", true),
                _createVNode(_component_v_btn, {
                  variant: "outlined",
                  color: _unref(settingsStore).settings.buttonColor,
                  class: "flex-1",
                  rounded: "lg",
                  onClick: closeDialog
                }, {
                  default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                    _createTextVNode(" Close ", -1)
                  ]))]),
                  _: 1
                }, 8, ["color"])
              ])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})